import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AUTH_API_URL = 'https://auth.ety.one/auth';
const STREAMLINE_API_URL = 'https://streamline-legacy.ety.one/api';
const PAYMENTS_API_BASE_URL = 'https://checkout.ety.one';
let refreshPromise = null;

const isTokenExpired = (token) => {
    const decodedToken = jwtDecode(token);
    return decodedToken.exp * 1000 < Date.now(); // exp is in seconds, Date.now() returns ms
};

const checkTokenAndRefresh = async () => {
    let token = localStorage.getItem("token");
    if (token && isTokenExpired(token)) {
        const refreshToken = localStorage.getItem("refreshToken");
        try {
            const data = await ApiService.refresh(refreshToken);
            localStorage.setItem("token", data.data.accessToken);
            localStorage.setItem("refreshToken", data.data.refreshToken);
            token = data.data.accessToken;
        } catch (error) {
            console.error("Failed to refresh token:", error);
            throw new Error("Token refresh failed");
        }
    }
    return token;
};

const forceTokenRefresh = () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const refreshToken = localStorage.getItem("refreshToken");
            try {
                const data = await ApiService.refresh(refreshToken);
                localStorage.setItem("token", data.data.accessToken);
                localStorage.setItem("refreshToken", data.data.refreshToken);
                return data.data.accessToken;
            } catch (error) {
                console.error("Failed to refresh token:", error);
                throw new Error("Token refresh failed");
            } finally {
                refreshPromise = null;
            }
        })();
    }

    return refreshPromise;
};

const ApiService = {
    // Existing methods
    login: async (username, password) => {
        try {
            const response = await axios.post(`${AUTH_API_URL}/login`, {
                username,
                password,
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    refresh: async (refreshToken) => {
        try {
            const response = await axios.post(`${AUTH_API_URL}/refresh?refreshToken=` + refreshToken, {
                refreshToken,
            });
            return response;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getCommands: async () => {
        try {
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.get(
                `${STREAMLINE_API_URL}/content/getCommands`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getContents: async (page, size) => {
        try {
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.get(
                `${STREAMLINE_API_URL}/content/getContents?page=${page}&size=${size}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // New method for getting contents by priority
    getContentsByPriority: async (page, size) => {
        try {
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.get(
                `${STREAMLINE_API_URL}/content/getContentsByPriority?page=${page}&size=${size}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // New method for getting content statistics
    getStats: async () => {
        try {
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.get(
                `${STREAMLINE_API_URL}/content/getStats`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    uploadFile: async (formData, options = {}) => {
        try {
            await forceTokenRefresh();
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.post(
                `${STREAMLINE_API_URL}/content/upload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent) => {
                        if (formData.onProgress && progressEvent.total) {
                            const percentCompleted = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            formData.onProgress(percentCompleted);
                        }
                    },
                    signal: options.signal,
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getSelf: async () => {
        try {
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.get(
                `${STREAMLINE_API_URL}/account/getSelf`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    verify: async (verifyKey) => {
        try {
            const accessToken = await checkTokenAndRefresh();
            const response = await axios.post(
                `${STREAMLINE_API_URL}/account/verify`,
                { key: verifyKey },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
};

export default ApiService;
